import { DescriptionOutlined } from '@mui/icons-material'
import { catalogColumnType, catalogFilterColumnType, catalogTabType } from '../catalogTypes'
import { sewpTrLineType } from 'src/types'
import { COLUMN_CONFIG_TR } from 'src/EditQuote/steps/validate/TechnicalRequest'

export type sewpItemType = {
  id: string
  clin: string
  manufacturer: string
  provider: string | undefined //manufacturer
  mfrPartNumber: string
  clinType: string | undefined //picklist -- service, product, maintenance, labor rate
  contractCeilingPrice: number | undefined
  sewpPrice: number | undefined //sewp contract ceiling price
  listPrice: number | undefined //msrp
  bma: string | undefined //picklist -- A or M
  taa: string | undefined //picklist -- C, NC, NA
  shortDescription: string | undefined
  longDescription: string | undefined
  discount: number | undefined
  validOn: Date
  backupDataFlag: string | undefined //default - null
  epeat: string | undefined //picklist -- B, G, S, NC, NA
  rebate: number | undefined
  energyStar: string | undefined //picklist -- C, NC, NA
  eolDate: Date
  discountClass: string | undefined
  discountSubClass: string | undefined
  productLine: string | undefined
  scrm: string | undefined //picklist -- A, M, N
  bundleFlag: string | undefined // textfield - default N
  visible: string | undefined //picklist -- Y or N
  unspsc: string | undefined
  ssPart: string | undefined
}

const ALL_SEWP_COLUMNS: catalogColumnType[] = [
  { key: 'clin', label: 'CLIN', width: '200px' },
  { key: 'manufacturer', label: 'Provider', width: '200px' },
  { key: 'mfrPartNumber', label: 'Part Number', width: '200px' },
  { key: 'clinType', label: 'CLIN Type', width: '100px' },
  {
    key: 'contractCeilingPrice',
    label: 'SEWP Price',
    width: '150px',
    inputType: 'currency',
    align: 'right',
  },
  {
    key: 'listPrice',
    label: 'List Price',
    width: '150px',
    inputType: 'currency',
    align: 'right',
  },
  { key: 'bma', label: 'BMA', width: '100px' },
  { key: 'taa', label: 'TAA', width: '100px' },
  { key: 'shortDescription', label: 'Short Description', width: '300px' },
  { key: 'longDescription', label: 'Long Decsription', width: '300px' },
  {
    key: 'discount',
    label: 'Discount (%)',
    width: '150px',
    inputType: 'percentage',
    align: 'right',
  },
  {
    key: 'validOn',
    label: 'Valid On',
    width: '150px',
    inputType: 'date',
  },
  { key: 'backupDataFlag', label: 'Backup Data Flag', width: '100px' },
  { key: 'epeat', label: 'EPEAT', width: '100px' },
  { key: 'rebate', label: 'Rebate', width: '100px' },
  { key: 'energyStar', label: 'Energy Star', width: '100px' },
  { key: 'eolDate', label: 'EOL Date', width: '100px' },
  { key: 'discountClass', label: 'Discount Class', width: '200px' },
  { key: 'discountSubClass', label: 'Discount SubClass', width: '200px' },
  { key: 'productLine', label: 'Product Line', width: '100px' },
  { key: 'scrm', label: 'SCRM', width: '100px' },
  { key: 'bundleFlag', label: 'Bundle Flag', width: '100px' },
  { key: 'visible', label: 'Visible', width: '100px' },
  { key: 'unspsc', label: 'UNSPSC', width: '100px' },
  { key: 'ssPart', label: 'SSPart', width: '100px' },
]

export const TR_SEWP_COLUMNS: catalogColumnType[] = [
  { key: 'check' as keyof sewpTrLineType, label: '', width: '48px', sticky: { left: '0px' } },
  { key: 'Clin', label: 'CLIN', width: '200px', sticky: { left: '48px' } },
  { key: 'QuoteNumber', label: 'Quote #', width: '150px' },
  { key: 'QuoteOwner', label: 'Quote Owner', width: '250px' },
  {
    key: 'ClinType',
    label: 'CLINTYPE',
    width: '100px',
    editable: true,
    options: COLUMN_CONFIG_TR['CLIN Type'].options,
  },
  {
    key: 'BundleFlag',
    label: 'BUNDLEFLAG',
    width: '150px',
    editable: true,
    inputType: 'boolean',
  },
  { key: 'Provider', label: 'PROVIDER', width: '100px' },
  { key: 'PartNumber', label: 'PARTNUMBER', width: '150px' },
  { key: 'ModelName', label: 'MODELNAME', width: '150px', editable: true, limit: 80 },
  { key: 'ProductLine', label: 'PRODUCTLINE', width: '150px', editable: true, limit: 80 },
  { key: 'ClinQty', label: 'CLINQTY', width: '100px', editable: true, inputType: 'number' },
  {
    key: 'ClinUnit',
    label: 'CLINUNIT',
    width: '100px',
    editable: true,
    options: [
      { label: 'Each', value: 'Each' },
      { label: 'Inch', value: 'Inch' },
      { label: 'Foot', value: 'Foot' },
      { label: 'Yard', value: 'Yard' },
      { label: 'Meter', value: 'Meter' },
      { label: 'Hour', value: 'Hour' },
      { label: 'Day', value: 'Day' },
      { label: 'Month', value: 'Month' },
      { label: 'Year', value: 'Year' },
      { label: 'Seat', value: 'Seat' },
    ],
  },
  { key: 'ScrmFlag', label: 'SCRMFLAG', width: '100px', editable: true, inputType: 'boolean' },
  { key: 'ScrmComment', label: 'SCRMCOMMENT', width: '250px', editable: true, limit: 80 },
  { key: 'DiscountClass', label: 'DISCOUNTCLASS', width: '150px' },
  { key: 'DiscountSubClass', label: 'DISCOUNTSUBCLASS', width: '200px' },
  { key: 'ProductClass', label: 'PRODUCTCLASS', width: '150px', editable: true, limit: 100 },
  {
    key: 'MaFlag',
    label: 'MAFLAG',
    width: '100px',
    editable: true,
    options: [
      { label: 'M | Mandatory', value: 'M' },
      { label: 'A | Available CLIN', value: 'A' },
    ],
  },
  {
    key: 'ListPrice',
    label: 'LISTPRICE',
    width: '100px',
    inputType: 'currency',
  },
  {
    key: 'SewpPrice',
    label: 'SEWPPRICE',
    width: '100px',
    inputType: 'currency',
  },
  { key: 'GsaPrice', label: 'GSAPRICE', width: '100px', inputType: 'currency' },
  { key: 'Rebate', label: 'REBATE', width: '100px', editable: true, inputType: 'currency' },
  {
    key: 'DiscountPercent',
    label: 'DISCOUNTPERCENT',
    width: '150px',
    inputType: 'percentage',
  },
  { key: 'EpeatFlag', label: 'EPEATFLAG', width: '100px' },
  { key: 'EnergyStarFlag', label: 'ENERGYSTARFLAG', width: '150px' },
  { key: 'BackupDataFlag', label: 'BACKUPDATAFLAG', width: '100px', editable: true, limit: 80 },
  { key: 'TaaFlag', label: 'TAAFLAG', width: '100px' },
  {
    key: 'VisibleFlag',
    label: 'VISIBLEFLAG',
    width: '150px',
    editable: true,
    inputType: 'boolean',
  },
  {
    key: 'ProductCondition',
    label: 'PRODUCTCONDITION',
    width: '150px',
    editable: true,
    options: [
      { label: 'N | New', value: 'N' },
      { label: 'U | Used', value: 'U' },
      { label: 'R | Refurbished', value: 'R' },
    ],
  },
  { key: 'ItemDescription', label: 'ITEMDESCRIPTION', width: '250px' },
  { key: 'ShortDesc', label: 'SHORTDESCRIPTION', width: '250px', editable: true, limit: 80 },
  { key: 'EolDate', label: 'EOLDATE', width: '100px', editable: true, inputType: 'date' },
  { key: 'EquivalentClin', label: 'EQUIVALENTCLIN', width: '150px', editable: true, limit: 80 },
  { key: 'ReplacementClin', label: 'REPLACEMENTCLIN', width: '150px', editable: true, limit: 80 },
  { key: 'BmReasonCode', label: 'BMREASONCODE', width: '150px', editable: true, limit: 80 },
  { key: 'BundlePart', label: 'BUNDLEPART', width: '150px', editable: true, limit: 80 },
  { key: 'SsPart', label: 'SSPART', width: '250px' },
  { key: 'Unspsc', label: 'UNSPSC', width: '100px' },
]

const TR_AWAITING_COLUMNS: catalogColumnType[] = [
  { key: 'TrNumber', label: 'TRNUMBER', width: '100px' },
  { key: 'ContractNumber', label: 'CONTRACTNO', width: '100px' },
  { key: 'TotalClins', label: 'TOTALCLINS', width: '100px' },
  { key: 'TrTxt', label: 'Uploaded File', width: '100px' },
]

const TR_APPROVED_COLUMNS: catalogColumnType[] = [
  ...TR_AWAITING_COLUMNS.filter(col => col.key !== 'TrTxt'),
  { key: 'SewpEmail', label: 'SEWP Approval Email', width: '150px' },
]

export const sewpColumnMapping = new Map<string, catalogColumnType[]>([
  ['nng15sd41b', ALL_SEWP_COLUMNS],
  ['nng15sd41b:TRS (Approved)', TR_APPROVED_COLUMNS],
  ['nng15sd41b:TRS (Awaiting)', TR_AWAITING_COLUMNS],
  ['nng15sd41b:TRS (Pending)', TR_SEWP_COLUMNS],
  ['nng15sc86b', ALL_SEWP_COLUMNS],
  ['nng15sc86b:TRS (Approved)', TR_APPROVED_COLUMNS],
  ['nng15sc86b:TRS (Awaiting)', TR_AWAITING_COLUMNS],
  ['nng15sc86b:TRS (Pending)', TR_SEWP_COLUMNS],
])

export const sewpTabGroups = new Map<string, catalogTabType[]>([
  [
    'nng15sd41b',
    [
      { label: 'NNG15SD41B', icon: <DescriptionOutlined />, source: 'nng15sd41b' },
      { label: 'TRS (Approved)', source: 'nng15sd41b/tr/headers', filters: { Status: 'Approved' }, isTrHeader: true },
      { label: 'TRS (Awaiting)', source: 'nng15sd41b/tr/headers', filters: { Status: 'Awaiting' }, isTrHeader: true },
      { label: 'TRS (Pending)', source: 'nng15sd41b/tr', filters: { Status: 'Pending' } },
    ],
  ],
  [
    'nng15sc86b',
    [
      {
        label: 'NNG15SC86B',
        icon: <DescriptionOutlined />,
        source: 'nng15sc86b',
      },
      { label: 'TRS (Approved)', source: 'nng15sc86b/tr/headers', filters: { Status: 'Approved' }, isTrHeader: true },
      { label: 'TRS (Awaiting)', source: 'nng15sc86b/tr/headers', filters: { Status: 'Awaiting' }, isTrHeader: true },
      { label: 'TRS (Pending)', source: 'nng15sc86b/tr', filters: { Status: 'Pending' } },
    ],
  ],
])

export const sewpColumnFilter = new Map<string, catalogFilterColumnType[]>([
  [
    'nng15sd41b',
    [
      { key: 'clin', label: 'CLIN' },
      { key: 'provider', label: 'Provider' },
      { key: 'mfrPartNumber', label: 'Part Number' },
      { key: 'shortDescription', label: 'Short Description' },
      { key: 'discountClass', label: 'Discount Class' },
      { key: 'discountSubClass', label: 'Discount SubClass' },
      { key: 'productLine', label: 'Product Line' },
      { key: 'unspsc', label: 'UNSPSC' },
      { key: 'ssPart', label: 'SSPART' },
    ],
  ],
  [
    'nng15sc86b',
    [
      { key: 'clin', label: 'CLIN' },
      { key: 'provider', label: 'Provider' },
      { key: 'mfrPartNumber', label: 'Part Number' },
      { key: 'shortDescription', label: 'Short Description' },
    ],
  ],
])
